import React, { Fragment, useEffect, useState } from 'react';
import T from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { get, isEmpty } from 'lodash-es';
import SimpleFormat from '@16g/react-simple-format';

import skeletons from './Skeletons';
import Button from '../../../Buttons/Button';
import SwitchStores from '../../../SwitchStores';
import Xray from '../../../../pages/app-insights/_shared/components/Xray';
import Screenshots from '../../../../pages/app-insights/_shared/components/Screenshots';
import InstalledSdks from '../../../../pages/app-insights/_shared/components/InstalledSdks';
import InsightsCardBox from '../../../../pages/app-insights/_shared/components/InsightsCardBox';
import RenderEmptyState from '../../../../pages/app-insights/_shared/components/RenderEmptyState';

import { requestAppQuickViewDetails } from '../../actions';
import { quickViewDataSelector } from '../../selectors';
import links from '../../configs/links';
import { isUnified } from '../../../../utils/helpers';

const Details = ({ app, settings }) => {
  const dispatch = useDispatch();
  const [detailsStore, setDetailsStore] = useState(
    isUnified(app.store) ? 'itunes_connect' : app.store
  );

  const data = useSelector(quickViewDataSelector);
  const app_details = get(data, ['app_details', detailsStore], {});
  const about_data = get(app_details, 'app_about.about_data');
  const description = get(about_data, 'description');
  const screenshotUrls = get(app_details, 'app_about.about_data.screenshot_urls', []);
  const screenshotCDNs = get(app_details, 'app_about.about_data.screenshot_cdn_paths', []);
  const sdks = get(app_details, 'installed_sdks') || [];
  const tags = get(app_details, 'x_ray_tags') || [];
  const isEmptyData = !settings.pending && isEmpty(app_details);
  const showStoreSwitcher = isUnified(app.store);
  const [languageOpen, setLanguageOpen] = useState(false);

  const hasMoreDescription = description && description.length > 230;
  const screenshots = screenshotCDNs.length ? screenshotCDNs : screenshotUrls;

  useEffect(() => {
    if (showStoreSwitcher && detailsStore === 'google_play') {
      dispatch(requestAppQuickViewDetails(detailsStore));
    }
  }, [detailsStore]);

  if (isEmptyData) {
    return <RenderEmptyState />;
  }

  return (
    <div className="app-quick-view__details">
      {showStoreSwitcher && (
        <div className="app-quick-view__details-switcher">
          {settings.pending ? (
            skeletons.detailsStoreSwitcher
          ) : (
            <SwitchStores active={detailsStore} onChange={(value) => setDetailsStore(value)} />
          )}
        </div>
      )}
      <div className="app-quick-view__details-description">
        {settings.pending ? (
          skeletons.detailsDescription
        ) : (
          <div className="show-more">
            <div className={cx('show-more-box', { open: languageOpen })}>
              <SimpleFormat text={description} />
            </div>
            {hasMoreDescription && (
              <div className="d-flex justify-content-start">
                <p className="show-more-box-btn" onClick={() => setLanguageOpen((prev) => !prev)}>
                  Show {!languageOpen ? 'More' : 'Less'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="app-quick-view__details-screenshots">
        {settings.pending ? (
          skeletons.detailsSlider
        ) : (
          <Fragment>
            <p className="paragraph">Screenshots</p>
            <Screenshots pending={settings.pending} screenshots={screenshots} />
          </Fragment>
        )}
      </div>
      <div className="app-quick-view__details-installed-sdks">
        {settings.pending ? (
          skeletons.detailsTable
        ) : (
          <InsightsCardBox
            headerLeft={<div className="paragraph">Installed SDKs</div>}
            headerRight={
              <Button variant="secondary" size="sm" href={links.sdks(app.store, app.ref_no)}>
                Go to SDKs
              </Button>
            }
          >
            <InstalledSdks sdks={sdks} store={detailsStore} withFunction={false} />
          </InsightsCardBox>
        )}
      </div>
      <div className="app-quick-view__details-features">
        {settings.pending ? (
          skeletons.detailsTable
        ) : (
          <InsightsCardBox
            headerLeft={<div className="paragraph">Features (X-Ray)</div>}
            headerRight={
              <Button variant="secondary" size="sm" href={links.xray(app.store, app.ref_no)}>
                View All
              </Button>
            }
          >
            <Xray tags={tags} store={detailsStore} />
          </InsightsCardBox>
        )}
      </div>
    </div>
  );
};

Details.propTypes = {
  app: T.object,
  settings: T.object.isRequired,
};

export default Details;
